import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import { CajaEmoji } from '#components/mobile/travesia/CajaEmoji';
import {
  leerTravesia,
  travesiaPendiente,
} from '#components/mobile/travesia/estado';
import type { GaviEstado } from '#components/mobile/travesia/Gavi';
import { useTravesia } from '#components/mobile/travesia/TravesiaContext';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { color, movimiento } from '#components/mobile/ui/tokens';
import { useSelector } from '#redux';

import {
  ediciones,
  edicionPendiente,
  marcarVista,
  versionVista,
} from './catalogo';
import type { Edicion } from './catalogo';

/** Lo que aplaude Gavi antes de que se cierre la hoja. */
const ESPERA_CIERRE = 700;

type NovedadesProps = {
  /** Otra ventana de una vez (el permiso de uso) va primero. */
  esperar?: boolean;
  /** Ya no hay novedades que enseñar (vistas o sobraban): van las siguientes. */
  onCerrado?: () => void;
};

/**
 * «Novedades a bordo»: la primera vez que se abre Rumbo tras actualizar,
 * Gavi cuenta en una hoja lo importante de la versión nueva. Sale una vez
 * por versión y dispositivo. Quien estrena la app ya tiene la travesía: si
 * la travesía estaba pendiente, al acabar se da la versión por vista.
 */
export function Novedades({ esperar = false, onCerrado }: NovedadesProps) {
  const { t } = useTranslation();
  const travesiaActiva = !!useTravesia()?.activa;
  const raw = useSelector(state => state.prefs.synced.travesia);
  const ocupada = travesiaActiva || travesiaPendiente(leerTravesia(raw));
  const [edicion, setEdicion] = useState(() =>
    edicionPendiente(ediciones(t), versionVista()),
  );

  // Recién instalada: la travesía ya explica la app; sus novedades sobran.
  const huboTravesia = useRef(false);
  useEffect(() => {
    if (ocupada) {
      huboTravesia.current = true;
    } else if (huboTravesia.current && edicion) {
      marcarVista(edicion.version);
      setEdicion(null);
      onCerrado?.();
    }
  }, [ocupada, edicion, onCerrado]);

  if (!edicion || ocupada || esperar) {
    return null;
  }

  return (
    <HojaNovedades
      edicion={edicion}
      onCerrar={() => {
        marcarVista(edicion.version);
        setEdicion(null);
        onCerrado?.();
      }}
    />
  );
}

const entra = keyframes({
  from: { opacity: 0, transform: 'translateY(10px)' },
});

const claseFila = css({
  animation: `${entra} .45s ${movimiento.muelle} backwards`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

type HojaNovedadesProps = {
  edicion: Edicion;
  onCerrar: () => void;
};

/** La hoja en sí (sin estado), para poder probarla y hacerle capturas. */
export function HojaNovedades({ edicion, onCerrar }: HojaNovedadesProps) {
  const { t } = useTranslation();
  const [gesto, setGesto] = useState<GaviEstado>('vuela');
  const [cerrando, setCerrando] = useState(false);

  function aNavegar() {
    if (cerrando) {
      return;
    }
    setCerrando(true);
    setGesto('aplaude');
    window.setTimeout(onCerrar, ESPERA_CIERRE);
  }

  return (
    <Hoja
      aria-label={t('New on board')}
      data-testid="novedades"
      onCerrar={onCerrar}
    >
      <View style={{ paddingTop: 4 }}>
        <Bocadillo
          variante="hoja"
          texto={edicion.saludo}
          estado={gesto}
          clave={gesto}
        />
        <Text
          style={{
            alignSelf: 'flex-start',
            marginTop: 14,
            padding: '4px 9px',
            borderRadius: 999,
            fontSize: 11.5,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: color.accent,
            backgroundColor: color.accentSoft,
          }}
        >
          <Trans>Version {{ version: edicion.version }}</Trans>
        </Text>
        <Text
          role="heading"
          aria-level={2}
          style={{
            margin: '6px 0 12px',
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            color: color.fg,
          }}
        >
          <Trans>New on board</Trans>
        </Text>
        <View style={{ gap: 10, marginBottom: 16 }}>
          {edicion.novedades.map((novedad, i) => (
            <View
              key={novedad.titulo}
              className={claseFila}
              data-testid="novedad"
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
                animationDelay: `${900 + Math.min(i, 4) * 250}ms`,
              }}
            >
              <CajaEmoji
                emoji={novedad.emoji}
                hueco={novedad.hueco}
                size={44}
              />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{ fontSize: 15, fontWeight: 700, color: color.fg }}
                >
                  {novedad.titulo}
                </Text>
                <Text
                  style={{
                    fontSize: 13.5,
                    lineHeight: 1.35,
                    color: color.fg2,
                    marginTop: 1,
                  }}
                >
                  {novedad.texto}
                </Text>
              </View>
            </View>
          ))}
        </View>
        <Boton bloque data-testid="novedades-ok" onPress={aNavegar}>
          <Trans>Set sail!</Trans>
        </Boton>
      </View>
    </Hoja>
  );
}
